// components/admin/Sidebar.js
import React, { useState, useEffect } from "react";
import { Nav } from "react-bootstrap";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import {
  House,
  QuestionCircle,
  People,
  Calendar,
  FileText,
  Person,
  ChevronLeft,
  ChevronRight,
  Gear,
  BoxArrowRight,
  List,
  X,
} from "react-bootstrap-icons";
import ieoLogo from "../../assets/ieo.png";

const Sidebar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { adminData } = useAuth();
  const isSuperAdmin = adminData?.role === "super_admin";
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [hoveredItem, setHoveredItem] = useState(null);

  // Check if mobile screen
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
      if (window.innerWidth > 768) {
        setIsMobileMenuOpen(false);
      }
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const isActive = (path) => {
    return location.pathname.startsWith(path);
  };
  const handleLogout = () => {
    localStorage.removeItem("adminToken");
    navigate("/admin/login");
  };

  const menuItems = [
    { path: "/admin", label: "Dashboard", icon: <House size={20} /> },
    {
      path: "/admin/questions",
      label: "Questions",
      icon: <QuestionCircle size={20} />,
    },
    { path: "/admin/students", label: "Students", icon: <People size={20} /> },
    {
      path: "/admin/appointments",
      label: "Appointments",
      icon: <Calendar size={20} />,
    },
    // {
    //   path: "/admin/documents",
    //   label: "Documents",
    //   icon: <FileText size={20} />,
    // },
    {
      path: "/admin/users",
      label: "User Management",
      icon: <Person size={20} />,
    },

    {
      path: "/admin/logout",
      label: "Logout",
      icon: <BoxArrowRight size={20} />,
      isLogout: true,
      onClick: handleLogout,
    },
  ];

  // Filter menu based on role: hide Questions and User Management from non-super-admins
  const visibleMenuItems = menuItems.filter((item) => {
    if (item.path === "/admin/questions" || item.path === "/admin/users") {
      return isSuperAdmin;
    }
    return true;
  });

  const toggleSidebar = () => {
    if (isMobile) {
      setIsMobileMenuOpen(!isMobileMenuOpen);
    } else {
      setIsCollapsed(!isCollapsed);
    }
  };

  const closeMobileMenu = () => {
    if (isMobile) {
      setIsMobileMenuOpen(false);
    }
  };

  return (
    <>
      {/* Mobile Menu Toggle Button */}
      {isMobile && (
        <button
          onClick={toggleSidebar}
          className="btn btn-light position-fixed top-0 start-0 m-3 z-1100 rounded-circle shadow-sm"
          style={{
            width: "45px",
            height: "45px",
            zIndex: 1100,
          }}
        >
          {isMobileMenuOpen ? <X size={20} /> : <List size={20} />}
        </button>
      )}

      {/* Overlay for mobile */}
      {isMobile && isMobileMenuOpen && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100 bg-dark bg-opacity-50 z-1099"
          onClick={closeMobileMenu}
          style={{ zIndex: 1099 }}
        />
      )}

      {/* Sidebar */}
      <div
        className={`sidebar position-fixed start-0 top-0 h-100 shadow-lg transition-all z-1100 ${
          isCollapsed ? "collapsed" : ""
        } ${isMobile && !isMobileMenuOpen ? "mobile-hidden" : ""}`}
        style={{
          width: isMobile ? "280px" : isCollapsed ? "70px" : "260px",
          zIndex: 1100,
          transition: isMobile
            ? "transform 0.3s ease-in-out"
            : "width 0.3s ease-in-out",
          background: "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)",
          borderRight: "1px solid #e2e8f0",
          transform:
            isMobile && !isMobileMenuOpen
              ? "translateX(-100%)"
              : "translateX(0)",
        }}
      >
        {/* Toggle Button - Desktop */}
        {!isMobile && (
          <div className="d-flex justify-content-end p-3">
            <button
              onClick={toggleSidebar}
              className="btn btn-outline-secondary btn-sm rounded-circle p-1 border"
              style={{
                width: "32px",
                height: "32px",
                transition: "all 0.3s",
              }}
              title={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            >
              {isCollapsed ? (
                <ChevronRight size={16} />
              ) : (
                <ChevronLeft size={16} />
              )}
            </button>
          </div>
        )}

        {/* Logo/Brand */}
        <div className="text-center mb-3 px-3 pt-3">
          {!isCollapsed || isMobile ? (
            <>
              <div className="mb-3">
                <img
                  src={ieoLogo}
                  alt="Ireland Education Office Logo"
                  className="img-fluid shadow-sm"
                  style={{
                    maxWidth: "140px",
                    height: "auto",
                    objectFit: "contain",
                  }}
                />
              </div>
              <h4 className="mb-1 fw-bold" style={{ color: "#4f46e5" }}>
                Visa Prediction Portal
              </h4>
              <small
                className="text-muted"
                style={{ fontSize: "0.75rem", letterSpacing: "1px" }}
              >
                ADMIN PANEL
              </small>
            </>
          ) : (
            <div className="mb-5">
              <img
                src={ieoLogo}
                alt="IEO Logo"
                className="img-fluid shadow-sm"
                style={{
                  width: "40px",
                  height: "40px",
                  objectFit: "contain",
                }}
              />
            </div>
          )}
        </div>

        {/* Navigation Menu with Scrollable Area */}
        <div
          className="flex-grow-1 px-3"
          style={{
            overflowY: "auto",
            overflowX: "hidden",
            height: "calc(100vh - 220px)",
            paddingBottom: "20px",
          }}
        >
          <Nav className="flex-column">
            {visibleMenuItems.map((item) => (
              <Nav.Link
                key={item.label}
                as={item.isLogout ? "button" : Link}
                to={!item.isLogout ? item.path : undefined}
                onClick={() => {
                  if (item.isLogout) item.onClick();
                  closeMobileMenu();
                }}
                className={`mb-2 d-flex align-items-center text-decoration-none position-relative ${
                  isActive(item.path) ? "active-menu" : "inactive-menu"
                }`}
                style={{
                  borderRadius: "10px",
                  padding: "12px 16px",
                  transition: "all 0.3s ease",
                  background: item.isLogout
                    ? "rgba(239, 68, 68, 0.1)" // light red
                    : isActive(item.path)
                      ? "linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)"
                      : "transparent",
                  border: item.isLogout
                    ? "1px solid rgba(239, 68, 68, 0.3)"
                    : isActive(item.path)
                      ? "1px solid rgba(79, 70, 229, 0.2)"
                      : "1px solid transparent",
                  color: item.isLogout
                    ? "#dc2626"
                    : isActive(item.path)
                      ? "white"
                      : "#475569",
                }}
              >
                <span
                  className="d-flex align-items-center justify-content-center"
                  style={{
                    minWidth: "24px",
                    transition: "all 0.3s",
                  }}
                >
                  {React.cloneElement(item.icon, {
                    className: isActive(item.path)
                      ? "text-white"
                      : "text-slate-600",
                    style: {
                      filter: isActive(item.path)
                        ? "brightness(0) invert(1)"
                        : "none",
                    },
                  })}
                </span>

                {(!isCollapsed || isMobile) && (
                  <span
                    className="ms-3 fw-medium"
                    style={{
                      fontSize: "0.95rem",
                      transition: "opacity 0.3s",
                      opacity: isCollapsed && !isMobile ? 0 : 1,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {item.label}
                  </span>
                )}

                {/* Active indicator for desktop */}
                {isActive(item.path) && !isCollapsed && !isMobile && (
                  <div
                    className="position-absolute end-0 top-50 translate-middle-y"
                    style={{
                      width: "4px",
                      height: "20px",
                      backgroundColor: "#4f46e5",
                      borderRadius: "2px 0 0 2px",
                      right: "-16px",
                    }}
                  />
                )}

                {/* Tooltip for collapsed state (desktop only) */}
                {isCollapsed && !isMobile && hoveredItem === item.path && (
                  <div
                    className="position-absolute start-full top-50 translate-middle-y ms-3 bg-white text-gray-800 px-3 py-2 rounded shadow-lg border"
                    style={{
                      zIndex: 9999,
                      fontSize: "0.875rem",
                      whiteSpace: "nowrap",
                      minWidth: "150px",
                    }}
                  >
                    {item.label}
                  </div>
                )}
              </Nav.Link>
            ))}
          </Nav>
        </div>
      </div>

      {/* Add padding to main content based on sidebar state */}
      <style jsx>{`
        .sidebar {
          scrollbar-width: thin;
          scrollbar-color: #cbd5e1 transparent;
        }

        .sidebar::-webkit-scrollbar {
          width: 6px;
        }

        .sidebar::-webkit-scrollbar-track {
          background: transparent;
          border-radius: 3px;
        }

        .sidebar::-webkit-scrollbar-thumb {
          background-color: #cbd5e1;
          border-radius: 3px;
        }

        .sidebar::-webkit-scrollbar-thumb:hover {
          background-color: #94a3b8;
        }

        .inactive-menu:hover {
          background: linear-gradient(
            135deg,
            rgba(79, 70, 229, 0.05) 0%,
            rgba(124, 58, 237, 0.05) 100%
          ) !important;
          border-color: rgba(79, 70, 229, 0.1) !important;
          color: #4f46e5 !important;
          transform: translateX(5px);
        }

        .active-menu::before {
          content: "";
          position: absolute;
          left: 0;
          top: 50%;
          transform: translateY(-50%);
          width: 4px;
          height: 70%;
          background: #4f46e5;
          border-radius: 0 4px 4px 0;
          opacity: ${isCollapsed && !isMobile ? 1 : 0};
        }

        @media (max-width: 768px) {
          .sidebar {
            transform: translateX(-100%);
            width: 280px !important;
          }

          .sidebar.mobile-hidden {
            transform: translateX(-100%);
          }

          .sidebar:not(.mobile-hidden) {
            transform: translateX(0);
          }
        }

        /* Ensure main content doesn't go under sidebar */
        @media (min-width: 769px) {
          body {
            padding-left: ${isCollapsed ? "70px" : "250px"};
            transition: padding-left 0.3s ease-in-out;
          }
        }
      `}</style>
    </>
  );
};

export default Sidebar;
